import { getDefaultStore } from 'jotai';

import { type ChronicleDeviceState, chronicleDeviceStateAtom } from '@/atoms/chronicle';
import { extractDeviceModel } from '@/utils/chronicle/device-helpers';

export type ChronicleDeviceProps = Record<string, string | number | boolean>;

/**
 * Explicit "value unavailable" marker for the device snapshot. An absent key
 * is ambiguous in dashboards (old app version? enrichment bug? device
 * disconnected?); a literal `NA` proves the enrichment ran and the value was
 * genuinely unavailable at fire time. Snowplow is the exception — its Iglu
 * schemas type battery/rssi as `integer | null`, so `NA` values are stripped
 * before the Snowplow merge (see the dispatchers).
 */
export const CHRONICLE_DEVICE_PROP_NA = 'NA';

/**
 * Live Chronicle (Heidi Remote) device-state snapshot, attached to every
 * `hardware_*` / `native_firmware_*` analytics event and to Chronicle-scoped
 * Datadog logs (via `chronicleLogger`, under `heidi_remote_device`) for APP-6285.
 *
 * Desktop analog of iOS `HardwareAnalytics.trackWithDeviceInfo`
 * (`Analytics+HardwareEvents.swift:201-223`). The four `hardware_*` keys match
 * the iOS field names exactly for parity; the remaining keys add the
 * loop-diagnostic device state (storage, chunked-session capability,
 * connection / recording status) that iOS does not telemeter.
 *
 * Every key is ALWAYS present: null / not-yet-probed values are sent as `NA`
 * (unlike iOS, which omits them) so an event can never be ambiguous about
 * whether enrichment ran. Battery uses `null` for "not yet read" (no `0`
 * placeholder), so a genuine 0% reading is kept and only true unknowns
 * become `NA`.
 *
 * `hardware_rssi` (dBm) is a live connected-RSSI read on macOS (the same
 * `readRSSI` mechanism iOS uses), refreshed at connect + the 60s health tick;
 * always `NA` on Windows (WinRT exposes no connected-RSSI API). `hardware_scan_rssi`
 * is the broadcast-scale advertisement RSSI from the preceding scan (the only
 * RSSI on Windows) — kept as a separate field so the two measurements (which
 * differ by ~10-15 dB) aren't conflated. Both ride on every BLE-related
 * event/log for link-quality / device-distance analysis.
 */
export function buildChronicleDeviceProps(
  state: ChronicleDeviceState
): ChronicleDeviceProps {
  const NA = CHRONICLE_DEVICE_PROP_NA;

  // serialNumber == the BLE advertised device name on this firmware (matches iOS).
  const serial = state.serialNumber ?? state.deviceName;

  return {
    hardware_serial_number: serial ?? NA,
    hardware_fw_version: state.firmwareVersion ?? NA,
    // `null` = not yet read (Rust sends no `0` placeholder anymore), so a
    // genuine 0% battery — a plausible bulk-sync-loop root cause — stays
    // observable and only a true not-yet-read becomes NA.
    hardware_battery_level: state.batteryLevel ?? NA,
    hardware_rssi: state.rssi ?? NA,
    // Distinct from hardware_rssi: advertisement-scale, scan-time, cross-platform
    // (the only RSSI available on Windows). Do NOT compare directly to hardware_rssi.
    hardware_scan_rssi: state.scanRssi ?? NA,
    device_model: extractDeviceModel(state.serialNumber ?? state.deviceName) ?? NA,
    connection_status: state.connectionStatus ?? NA,
    storage_total_mb: state.storageTotalMb ?? NA,
    storage_free_mb: state.storageFreeMb ?? NA,
    usb_mode_enabled: state.usbModeEnabled,
    recording_pre_existing: state.recordingPreExisting,
    supports_chunked_sessions: state.supportsChunkedSessions,
  };
}

/**
 * Reads the live device-state snapshot outside React (e.g. from the
 * `trackEventV2` enricher). Uses the Jotai default store — the same
 * outside-React access pattern already used in `_app.tsx`.
 */
export function getChronicleDeviceProps(): ChronicleDeviceProps {
  try {
    return buildChronicleDeviceProps(getDefaultStore().get(chronicleDeviceStateAtom));
  } catch {
    // Telemetry is best-effort: a store-read failure must never break the
    // log/event call this is enriching.
    return {};
  }
}

// The `hardware_*` / `native_firmware_*` event predicate lives in the
// @heidi/runtime/analytics pipeline (`enrich.ts`, CHRONICLE_EVENT_PREFIXES)
// since the P2 extraction — the app-side copy was deleted in ARCFE-688 so
// prefix changes can't silently land in a dead duplicate.
